import {
  ChevronDown,
  Shield,
  Zap,
  Users,
  ArrowRight,
  CheckCircle,
  Clock,
  MessageSquare,
  RefreshCw,
  FileText,
  AlertTriangle,
  Lock,
  Layers,
  Workflow,
  Building2,
  Search,
  Gauge,
} from "lucide-react";

export default function AxelLanding() {
  // ------------------------------------------------------------
  // CONTENT (same pattern as Cerebra/Lexa pages)
  // ------------------------------------------------------------

  const painPoints = [
    {
      icon: MessageSquare,
      title: "Client chasing never ends",
      description:
        "Missing bank access. Missing receipts. Missing approvals. Staff spend hours per week just following up — and month-end still slips.",
    },
    {
      icon: Workflow,
      title: "Workflows live across too many tools",
      description:
        "QuickBooks, Dext/Hubdoc, Bill.com, payroll, portals, spreadsheets, email, Karbon/Asana — nothing is truly connected end-to-end.",
    },
    {
      icon: AlertTriangle,
      title: "Exceptions explode at scale",
      description:
        "Transaction edge cases, mis-codes, duplicates, chargebacks, intercompany transfers — one-off issues become hundreds across 200 clients.",
    },
    {
      icon: Clock,
      title: "Month-end becomes a fire drill",
      description:
        "Reconciliations, accruals, AR/AP, sales tax, payroll liabilities — done under deadline pressure with inconsistent checklists and handoffs.",
    },
    {
      icon: Shield,
      title: "Security & audit risk blocks automation",
      description:
        "Firms can’t let an AI ‘do things’ without approvals, permissions, audit trails, and a private/security-first architecture.",
    },
    {
      icon: Gauge,
      title: "Partners lack firm-wide visibility",
      description:
        "No real-time view of which clients are behind, why they’re behind, which staff is overloaded, and what’s at risk this week.",
    },
  ];

  const howItWorks = [
    {
      icon: Layers,
      step: "Step 1",
      title: "Connect your firm stack",
      description:
        "Axel connects to QuickBooks Online and your workflow tools. It maps each client’s systems, permissions, and engagement rules.",
      example:
        '"Connected 214 clients. Mapped chart-of-accounts rules. Imported close checklists. Set approval policies by partner."',
    },
    {
      icon: Search,
      step: "Step 2",
      title: "Axel detects gaps + exceptions",
      description:
        "It continuously checks for missing docs, broken feeds, reconciliation mismatches, overdue AR/AP, and close blockers — firm-wide.",
      example:
        '"12 clients missing statements. 7 bank feeds disconnected. 19 uncategorized > $500. 4 sales tax deadlines in 10 days."',
    },
    {
      icon: Workflow,
      step: "Step 3",
      title: "Axel executes the workflow",
      description:
        "Axel chases clients, requests approvals, updates tasks, posts clean entries when allowed, and escalates only true exceptions.",
      example:
        '"Sent 38 client follow-ups. Collected 21 receipts. Updated 64 tasks. Prepared 9 reconciliation packets for review."',
    },
    {
      icon: Shield,
      step: "Step 4",
      title: "Governed actions + full audit trail",
      description:
        "Every action is permissioned, logged, and reversible. Configure approvals (by partner, senior, client) and keep an audit-ready trail.",
      example:
        '"Payment runs require partner approval. Journal entries require senior sign-off. All client communications archived to the binder."',
    },
  ];

  const whatItDoes = [
    {
      action: "Relentless client follow-ups",
      description: "Requests missing receipts, statements, approvals — and keeps nudging until done",
    },
    {
      action: "Month-end close orchestration",
      description: "Runs your close checklist, prepares working papers, flags variances, routes exceptions",
    },
    {
      action: "QuickBooks hygiene at scale",
      description: "Categorization suggestions + exception queues + rule enforcement across every client",
    },
    {
      action: "AP/AR ops coordination",
      description: "Invoice collection, approval routing, payment scheduling, AR reminders, escalation logic",
    },
    {
      action: "Sales tax & compliance readiness",
      description: "Tracks filing deadlines, gathers support, assembles returns packages, creates audit binder",
    },
    {
      action: "Firm-wide risk dashboard",
      description: "Shows what’s behind, what’s blocked, and what’s at risk — by client, staff, or partner",
    },
    {
      action: "Exception triage",
      description: "Flags edge cases with confidence scoring and routes them to the right reviewer",
    },
    {
      action: "Audit trail + evidence binder",
      description: "Every change and comm is logged with source docs and approvals attached",
    },
  ];

  const capabilities = [
    {
      icon: Lock,
      title: "Private + Permissioned by Design",
      description:
        "Axel runs in your firm’s security model. Least-privilege access, role-based actions, and client-by-client permission boundaries.",
    },
    {
      icon: CheckCircle,
      title: "Approvals, Not Guesswork",
      description:
        "Configure required approvals per action: entries, payments, client messages, filings. No silent changes. No surprises.",
    },
    {
      icon: FileText,
      title: "Audit-Ready Outputs",
      description:
        "Axel produces binder-ready support: source docs, explanations, and a time-stamped action log for every decision.",
    },
    {
      icon: RefreshCw,
      title: "Cross-Tool Execution",
      description:
        "Not ‘AI inside QuickBooks’. Axel moves work across email, portals, workflow tools, and QBO — end-to-end.",
    },
    {
      icon: Users,
      title: "Scales Across 100s of Clients",
      description:
        "Works like an engagement manager at scale: monitors every client, detects blockers, and pushes the workflow forward.",
    },
    {
      icon: Zap,
      title: "Exception-First Automation",
      description:
        "Automates the boring 80%, but routes the risky 20% with context, confidence, and recommended resolution paths.",
    },
  ];

  const realScenarios = [
    {
      scenario: "The Missing Docs Spiral",
      before:
        "Month-end is late because 40 clients didn’t upload receipts or statements. Staff are buried in follow-up emails and DMs.",
      after:
        "Axel requests missing items automatically, follows up on a schedule, updates the workflow tasks, and escalates only non-responsive clients.",
      win: "Fewer close delays + fewer staff hours wasted on chasing",
    },
    {
      scenario: "The Exception Avalanche",
      before:
        "One-off issues become hundreds: chargebacks, refunds, unclear merchants, mixed-use expenses, intercompany transfers.",
      after:
        "Axel flags exceptions with confidence scoring, proposes coding, attaches evidence, and routes to reviewers with everything pre-filled.",
      win: "Cleaner books, fewer errors, and faster review",
    },
    {
      scenario: "The Close Fire Drill",
      before:
        "Close checklists are inconsistent. People forget steps. Partners don’t know what’s blocked until it’s too late.",
      after:
        "Axel runs standardized close workflows, produces reconciliation packets, flags variances, and shows firm-wide close status in real time.",
      win: "Predictable closes and fewer surprise escalations",
    },
    {
      scenario: "The Audit Scramble",
      before:
        "When a client asks ‘why was this changed?’ the team digs through emails and Slack to reconstruct what happened.",
      after:
        "Axel keeps an evidence binder: approvals, source docs, action logs, explanations — automatically attached to the transaction.",
      win: "Audit-ready by default",
    },
  ];

  // ------------------------------------------------------------
  // UI
  // ------------------------------------------------------------

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero */}
      <header className="relative min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 overflow-hidden flex items-center justify-center">
        {/* Background blobs */}
        <div className="absolute top-20 right-20 w-72 h-72 bg-indigo-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute bottom-20 left-20 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>

        <div className="relative z-10 text-center px-4 max-w-6xl">
          <div className="inline-block mb-6">
            <span className="text-7xl animate-float">🧾</span>
          </div>

          <h1 className="text-6xl md:text-7xl font-bold text-white mb-6 leading-tight">Meet Axel</h1>

          <p className="text-2xl md:text-3xl text-slate-200 font-light mb-3">
            Your AI Engagement Manager for Accounting Firms
          </p>

          <p className="text-2xl md:text-3xl text-blue-200 font-semibold max-w-4xl mx-auto leading-tight mb-8">
            Not another AI tool inside QuickBooks.
            <br />
            <span className="text-white">Axel runs your firm’s workflows across every client.</span>
          </p>

          <p className="text-lg md:text-xl text-slate-300 max-w-4xl mx-auto leading-relaxed mb-6">
            Axel chases missing docs, triages exceptions, executes your close checklist, updates your workflow system,
            and keeps an audit-ready trail — with approvals and permissions.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
            <a
              href="mailto:nate@paireeo.com?subject=Axel%20Demo%20Request&body=Hi%20Nate%2C%0A%0AI%27m%20interested%20in%20learning%20more%20about%20Axel%20and%20would%20love%20to%20schedule%20a%20demo.%0A%0ALooking%20forward%20to%20connecting!"
              className="px-10 py-5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-xl font-semibold rounded-full hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center gap-2"
            >
              Request a Firm Demo <ArrowRight className="w-6 h-6" />
            </a>

            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20">
              <Shield className="w-4 h-4 text-emerald-300" />
              <span className="text-sm text-white font-semibold">Private • Permissioned • Audit-Ready</span>
            </div>
          </div>

          <p className="text-sm text-slate-400 mt-6">
            Built for large firms • QuickBooks Online • 100s of clients • Exception-first automation
          </p>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-8 h-8 text-white opacity-50" />
        </div>
      </header>

      <main className="relative">
        {/* Pain Points */}
        <section className="py-20 px-4 bg-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-5xl font-bold text-slate-900 mb-4 text-center">The Real Problem Isn’t Bookkeeping</h2>
            <p className="text-xl text-slate-600 text-center mb-16 max-w-3xl mx-auto">
              Large firms don’t need more dashboards. They need execution across hundreds of clients — with governance.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {painPoints.map((p, idx) => {
                const Icon = p.icon;
                return (
                  <div
                    key={idx}
                    className="bg-gradient-to-br from-slate-50 to-blue-50 border border-slate-200 rounded-2xl p-8 hover:shadow-lg hover:-translate-y-1 transition-all"
                  >
                    <div className="mb-4 p-3 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-lg w-fit">
                      <Icon className="w-6 h-6 text-blue-700" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">{p.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{p.description}</p>
                  </div>
                );
              })}
            </div>

            <div className="mt-14 bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-2xl p-10 text-center">
              <p className="text-2xl font-semibold mb-2">Axel’s job:</p>
              <p className="text-3xl font-bold">
                Keep every client moving forward — automatically — without breaking compliance.
              </p>
            </div>
          </div>
        </section>

        {/* What It Does */}
        <section className="py-20 px-4 bg-gradient-to-b from-slate-50 to-slate-100">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-5xl font-bold text-slate-900 mb-4 text-center">
              Not a Chatbot. A Firm Operations Layer.
            </h2>
            <p className="text-xl text-slate-600 text-center mb-16 max-w-3xl mx-auto">
              Axel doesn’t “suggest.” Axel executes workflows and closes loops — across systems, across clients.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              {whatItDoes.map((item, idx) => (
                <div
                  key={idx}
                  className="bg-white border-2 border-blue-200 rounded-xl p-6 hover:shadow-lg transition-all"
                >
                  <div className="flex items-start gap-4">
                    <CheckCircle className="w-6 h-6 text-blue-700 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 mb-2">{item.action}</h3>
                      <p className="text-slate-600">{item.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white rounded-2xl p-10 text-center">
              <p className="text-2xl font-semibold mb-2">The difference:</p>
              <p className="text-3xl font-bold">
                Traditional AI drafts notes. Axel completes workflows and proves it with an audit trail.
              </p>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-20 px-4 bg-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-5xl font-bold text-slate-900 mb-4 text-center">How Axel Works</h2>
            <p className="text-xl text-slate-600 text-center mb-16 max-w-3xl mx-auto">
              Four steps: connect, detect, execute, govern. Designed for large firms and real permissions.
            </p>

            <div className="space-y-8">
              {howItWorks.map((phase, idx) => {
                const Icon = phase.icon;
                return (
                  <div
                    key={idx}
                    className="bg-white rounded-2xl p-8 shadow-md border border-slate-200 overflow-hidden hover:shadow-lg transition-all"
                  >
                    <div className="flex items-start gap-6">
                      <div className="flex-shrink-0">
                        <div className="p-3 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-lg">
                          <Icon className="w-8 h-8 text-blue-700" />
                        </div>
                      </div>
                      <div className="flex-grow">
                        <div className="flex items-baseline gap-3 mb-2">
                          <span className="text-sm font-bold text-blue-700 uppercase">{phase.step}</span>
                          <h3 className="text-2xl font-bold text-slate-900">{phase.title}</h3>
                        </div>
                        <p className="text-slate-600 mb-4 text-lg">{phase.description}</p>
                        <div className="bg-slate-50 border-l-4 border-indigo-400 p-4 rounded italic text-slate-700">
                          {phase.example}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Core Capabilities */}
        <section className="py-20 px-4 bg-slate-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-5xl font-bold text-slate-900 mb-4 text-center">Built for Enterprise Firms</h2>
            <p className="text-xl text-slate-600 text-center mb-16 max-w-3xl mx-auto">
              Security, approvals, and auditability aren’t add-ons — they’re the product.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {capabilities.map((cap, idx) => {
                const Icon = cap.icon;
                return (
                  <div
                    key={idx}
                    className="bg-white rounded-2xl p-8 shadow-md hover:shadow-lg hover:-translate-y-2 transition-all border border-slate-200"
                  >
                    <div className="mb-4 p-3 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-lg w-fit">
                      <Icon className="w-6 h-6 text-blue-700" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-3">{cap.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{cap.description}</p>
                  </div>
                );
              })}
            </div>

            <div className="mt-14 bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-2xl p-10 text-center">
              <p className="text-2xl font-semibold text-slate-900 mb-2">Governance mode by default</p>
              <p className="text-slate-700 text-lg">
                Axel can run autonomously inside approved playbooks — or require approvals at each step. Your firm stays
                in control.
              </p>
            </div>
          </div>
        </section>

        {/* Real Scenarios */}
        <section className="py-20 px-4 bg-gradient-to-b from-white to-slate-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-5xl font-bold text-slate-900 mb-4 text-center">What Changes in Practice</h2>
            <p className="text-xl text-slate-600 text-center mb-16 max-w-3xl mx-auto">
              Axel doesn’t replace your CPAs. It replaces the coordination chaos that steals their time.
            </p>

            <div className="space-y-8">
              {realScenarios.map((story, idx) => (
                <div key={idx} className="bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-200">
                  <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white px-8 py-4">
                    <h3 className="text-2xl font-bold">{story.scenario}</h3>
                  </div>
                  <div className="p-8">
                    <div className="grid md:grid-cols-2 gap-8 mb-6">
                      <div>
                        <p className="text-sm font-bold text-slate-500 uppercase mb-2">Before Axel</p>
                        <p className="text-slate-700">{story.before}</p>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-slate-500 uppercase mb-2">With Axel</p>
                        <p className="text-slate-700">{story.after}</p>
                      </div>
                    </div>
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 p-4 rounded">
                      <p className="text-green-700 font-semibold text-lg">✅ {story.win}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ROI / Simple Math */}
        <section className="py-20 px-4 bg-slate-950 text-white">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-5xl font-bold mb-6">The Math Is Simple</h2>
            <p className="text-2xl text-slate-300 mb-12">
              One firm-wide automation layer beats 10 disconnected AI tools.
            </p>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="bg-slate-900 rounded-2xl p-8 border border-slate-800">
                <p className="text-slate-400 mb-2">Biggest Time Sink</p>
                <p className="text-4xl font-bold text-blue-300">Client Chasing</p>
                <p className="text-slate-400 mt-2">Receipts • statements • approvals</p>
              </div>
              <div className="bg-slate-900 rounded-2xl p-8 border border-slate-800">
                <p className="text-slate-400 mb-2">Biggest Risk</p>
                <p className="text-4xl font-bold text-indigo-300">Exceptions</p>
                <p className="text-slate-400 mt-2">Edge cases at scale</p>
              </div>
              <div className="bg-gradient-to-br from-emerald-600 to-teal-600 rounded-2xl p-8 border border-emerald-500">
                <p className="text-emerald-100 mb-2">Axel Outcome</p>
                <p className="text-4xl font-bold">Predictable Close</p>
                <p className="text-emerald-100 mt-2">Governed execution + audit trail</p>
              </div>
            </div>

            <p className="text-xl text-slate-300 max-w-4xl mx-auto">
              Axel reduces coordination overhead, standardizes close, and escalates only real exceptions — while keeping
              partners in control.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 px-4 bg-gradient-to-br from-blue-600 via-indigo-600 to-slate-900">
          <div className="max-w-5xl mx-auto">
            <div className="text-center text-white">
              <h2 className="text-5xl md:text-6xl font-bold mb-6">Ready to Run 200 Clients Like 20?</h2>
              <p className="text-2xl mb-4">Stop losing weeks to follow-ups and late closes.</p>
              <p className="text-xl mb-10 text-blue-100">
                Axel keeps every engagement moving — with approvals, permissions, and an audit-ready binder.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
                <a
                  href="mailto:nate@paireeo.com?subject=Axel%20Demo%20Request&body=Hi%20Nate%2C%0A%0AI%27m%20interested%20in%20learning%20more%20about%20Axel%20and%20would%20love%20to%20schedule%20a%20demo.%0A%0ALooking%20forward%20to%20connecting!"
                  className="px-10 py-5 bg-white text-blue-700 text-xl font-bold rounded-full hover:shadow-2xl hover:scale-105 transition-all flex items-center justify-center gap-2"
                >
                  Request a Firm Demo <ArrowRight className="w-6 h-6" />
                </a>

                <div className="px-10 py-5 bg-white/10 border border-white/20 text-white text-xl font-semibold rounded-full flex items-center justify-center gap-2">
                  <Building2 className="w-6 h-6" />
                  Built for Large Firms
                </div>
              </div>

              <p className="text-blue-100">
                Want the overview deck? Email <span className="font-semibold text-white">hello@paireeo.info</span>
              </p>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-slate-950 text-white py-12 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-lg font-semibold mb-2">Axel by Paireeo</p>
          <p className="text-slate-400 mb-2">Autonomous Firm Operations for Accounting</p>
          <p className="text-slate-500 text-sm mt-6">© 2026 Paireeo. All rights reserved.</p>
        </div>
      </footer>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
      `}</style>
    </div>
  );
}
